/**
 * Fake ClanWar snapshots for dev notification testing.
 * Structure matches Coc API / evaluateTestCwConditions input.
 * Tags use only CoC-allowed characters (0289PYLQGRJCUV), 5–12 chars.
 */
const FAKE_CLAN_TAG = "#C2R2P2Q2L";
const FAKE_OPPONENT_TAG = "#09Y8JQGR";
const MY_PLAYER_TAG = "#P2Q8Y9L";
const PLAYER_BELOW_TAG = "#RJCUV02";

function baseWarClan(
  members: {
    tag: string;
    name: string;
    mapPosition: number;
    townhallLevel: number;
    opponentAttacks: number;
    bestOpponentAttack: { stars?: number };
    attacks: { stars?: number }[];
  }[]
): {
  destructionPercentage: number;
  tag: string;
  name: string;
  badgeUrls: Record<string, unknown>;
  clanLevel: number;
  attacks: number;
  stars: number;
  expEarned: number;
  members: typeof members;
}
{
  return {
    destructionPercentage: 0,
    tag: FAKE_CLAN_TAG,
    name: "Fake Clan",
    badgeUrls: {},
    clanLevel: 10,
    attacks: 0,
    stars: 0,
    expEarned: 0,
    members,
  };
}

function baseOpponent(): ReturnType<typeof baseWarClan>
{
  return {
    destructionPercentage: 0,
    tag: FAKE_OPPONENT_TAG,
    name: "Fake Opponent",
    badgeUrls: {},
    clanLevel: 10,
    attacks: 0,
    stars: 0,
    expEarned: 0,
    members: [],
  };
}

export interface FakeClanWarShape
{
  clan: ReturnType<typeof baseWarClan>;
  teamSize: number;
  attacksPerMember: number;
  battleModifier: string;
  opponent: ReturnType<typeof baseOpponent>;
  startTime: string;
  state: string;
  endTime: string;
  preparationStartTime: string;
}

export interface FakeClanWarMemberShape
{
  tag: string;
  name: string;
  mapPosition: number;
  townhallLevel: number;
  opponentAttacks: number;
  bestOpponentAttack: { stars?: number };
  attacks: { stars?: number }[];
}

export function createFakeWarWithEndTime(
  endTime: Date,
  options: {
    myPlayerAttacksCount?: number;
    playerBelowAttacksCount?: number;
    allBelowDestroyed?: boolean;
  } = {}
): FakeClanWarShape
{
  const { myPlayerAttacksCount = 0, playerBelowAttacksCount = 0, allBelowDestroyed = false } = options;
  const now = endTime.toISOString();

  const myPlayer: FakeClanWarMemberShape = {
    tag: MY_PLAYER_TAG,
    name: "My Player",
    mapPosition: 1,
    townhallLevel: 14,
    opponentAttacks: 0,
    bestOpponentAttack: {},
    attacks: Array.from({ length: myPlayerAttacksCount }, () => ({ stars: 2 })),
  };

  const playerBelow: FakeClanWarMemberShape = {
    tag: PLAYER_BELOW_TAG,
    name: "Player Below",
    mapPosition: 2,
    townhallLevel: 14,
    opponentAttacks: 0,
    bestOpponentAttack: allBelowDestroyed ? { stars: 3 } : {},
    attacks: Array.from({ length: playerBelowAttacksCount }, () => ({ stars: 2 })),
  };

  const clan = baseWarClan([myPlayer, playerBelow]);
  const opponent = baseOpponent();

  return {
    clan,
    teamSize: 2,
    attacksPerMember: 2,
    battleModifier: "NONE",
    opponent,
    startTime: now,
    state: "inWar",
    endTime: endTime.toISOString(),
    preparationStartTime: now,
  };
}

export function createFakeClanMember(myPlayerAttacksCount: number): FakeClanWarMemberShape
{
  return {
    tag: MY_PLAYER_TAG,
    name: "My Player",
    mapPosition: 1,
    townhallLevel: 14,
    opponentAttacks: 0,
    bestOpponentAttack: {},
    attacks: Array.from({ length: myPlayerAttacksCount }, () => ({ stars: 2 })),
  };
}

export function createWarForTimeCheckpoint(endTime: Date): FakeClanWarShape
{
  return createFakeWarWithEndTime(endTime, { myPlayerAttacksCount: 0, playerBelowAttacksCount: 0 });
}

export function createWarForBelowAllDestroyed(endTime: Date): FakeClanWarShape
{
  return createFakeWarWithEndTime(endTime, { allBelowDestroyed: true });
}

export function createWarForPlayerBelowFirstAttack(endTime: Date): FakeClanWarShape
{
  return createFakeWarWithEndTime(endTime, { playerBelowAttacksCount: 1 });
}

export function createWarForPlayerBelowBothAttacks(endTime: Date): FakeClanWarShape
{
  return createFakeWarWithEndTime(endTime, { playerBelowAttacksCount: 2 });
}

export { MY_PLAYER_TAG, FAKE_CLAN_TAG };
